import {
  Breakpoint,
  ButtonSize,
  ButtonTheme,
  FontWeight,
  Markdown,
  Typography,
  TypographyElement,
  TypographyVariant,
  UseState,
} from '@elearning-platform/ui';
import React, { useContext, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { PageDataContext } from '../../components/page';
import { VideoOption } from '../../enums';
import { PageData, SelectVideosOption } from '../../interfaces';
import { VideoWithText } from '../videoWithText/videoWithText.component';

import QuestionMarks from './images/questionMarks.png';
import {
  ButtonsWrapper,
  ContentContainer,
  ContentWrapper,
  HeaderWrapper,
  Image,
  ImageWrapper,
  StyledButton,
  StyledHeaderTypography,
  VideoDivider,
  VideosWrapper,
  Wrapper,
} from './selectVideo.styled';
import { SelectVideoProps } from './selectVideo.types';

export const SelectVideo: React.FC<SelectVideoProps> = (props: SelectVideoProps): JSX.Element => {
  const { callComplete, videosData, baseUrl }: SelectVideoProps = props;
  const [selectedOption, setSelectedOption]: UseState<VideoOption | undefined> = useState<VideoOption | undefined>();
  const isSmallDesktop: boolean = useMediaQuery({
    query: Breakpoint.SmallDesktop,
  });
  const { t }: TransProps<never> = useTranslation();
  const dataCtx: Partial<PageData> | null = useContext(PageDataContext);
  const { title, description, slidesOverallCounter, currentSlideCounter }: Partial<PageData> = dataCtx ?? {};

  const handleVideoSelection: (selectedOption: VideoOption) => void = (selectedOption: VideoOption): void => {
    setSelectedOption(selectedOption);
  };

  return (
    <Wrapper>
      <HeaderWrapper>
        <StyledHeaderTypography variant={TypographyVariant.TextSM}>
          <Markdown
            text={t('competence.app.scrollLesson.componentsV2.selectVideo.slides', { currentSlideCounter, slidesOverallCounter })}
          />
        </StyledHeaderTypography>
        <StyledHeaderTypography fontWeight={FontWeight.Bold} variant={TypographyVariant.TextSM}>
          {t('competence.app.scrollLesson.componentsV2.selectVideo.movie')}
        </StyledHeaderTypography>
      </HeaderWrapper>
      {selectedOption ? (
        <VideosWrapper>
          {selectedOption !== VideoOption.SecondVideo && (
            <VideoWithText
              callComplete={callComplete}
              description={videosData.firstVideoData.description}
              headline={videosData.firstVideoData.title}
              videoSrc={baseUrl + videosData.firstVideoData.videoUrl}
            />
          )}
          {selectedOption === VideoOption.BothVideos && (
            <VideoDivider />
          )}
          {selectedOption !== VideoOption.FirstVideo && (
            <VideoWithText
              callComplete={callComplete}
              description={videosData.secondVideoData.description}
              headline={videosData.secondVideoData.title}
              videoSrc={baseUrl + videosData.secondVideoData.videoUrl}
            />
          )}
        </VideosWrapper>
      ) : (
        <ContentWrapper>
          <ContentContainer>
            {!!title && (
              <Typography
                fontWeight={FontWeight.Bold}
                tag={TypographyElement.div}
                variant={isSmallDesktop ? TypographyVariant.DisplaySM : TypographyVariant.TextXL}
              >
                <Markdown text={title} />
              </Typography>
            )}
            {!!description && (
              <Typography variant={isSmallDesktop ? TypographyVariant.DisplayXS : TypographyVariant.TextXL}>
                <Markdown text={description} />
              </Typography>
            )}
            {!isSmallDesktop && (
              <ImageWrapper>
                <Image src={QuestionMarks} />
              </ImageWrapper>
            )}
            <ButtonsWrapper>
              {videosData.options.map((option: SelectVideosOption): JSX.Element => (
                <StyledButton
                  fullWidth={!isSmallDesktop}
                  key={option.value}
                  label={option.label}
                  onClick={(): void => handleVideoSelection(option.value)}
                  size={ButtonSize.Large}
                  variant={ButtonTheme.SecondaryBrand}
                />
              ))}
            </ButtonsWrapper>
          </ContentContainer>
          {!!isSmallDesktop && (
            <ImageWrapper>
              <Image src={QuestionMarks} />
            </ImageWrapper>
          )}
        </ContentWrapper>
      )}
    </Wrapper>
  );
};
